import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Permit {
  id: string;
  org_id: string;
  job_id: string;
  jurisdiction_id: string | null;
  trade_code: string | null;
  permit_type: string;
  scope_summary: string | null;
  status: string;
  permit_number: string | null;
  application_number: string | null;
  applied_at: string | null;
  issued_at: string | null;
  expires_at: string | null;
  finaled_at: string | null;
  total_fees: number;
  fees_paid: number;
  applicant_name: string | null;
  contractor_license: string | null;
  property_address: string | null;
  ai_autofill: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface Jurisdiction {
  id: string;
  name: string;
  state: string;
  portal_type: string;
  portal_url: string | null;
  recipe_key: string | null;
  supports_online_payment: boolean;
  supports_online_filing: boolean;
}

export interface PermitInspection {
  id: string;
  permit_id: string;
  inspection_type: string;
  status: string;
  scheduled_at: string | null;
  completed_at: string | null;
  inspector_name: string | null;
  result_notes: string | null;
}

export interface PermitFee {
  id: string;
  permit_id: string;
  kind: string;
  description: string | null;
  amount: number;
  paid: boolean;
  paid_at: string | null;
  receipt_url: string | null;
}

export interface PermitFiling {
  id: string;
  permit_id: string;
  method: string;
  status: string;
  recipe_key: string | null;
  browserbase_replay_url: string | null;
  screenshots: string[];
  steps_log: Array<{ at: string; step: string; status: string; detail?: string }>;
  result: Record<string, unknown>;
  error_message: string | null;
  cost_cents: number;
  markup_cents: number;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
}

const STATUS_LABELS: Record<string, string> = {
  draft: "Draft",
  prepping_docs: "Prepping docs",
  ready_to_submit: "Ready to submit",
  submitted: "Submitted",
  under_review: "Under review",
  corrections_required: "Corrections required",
  approved: "Approved",
  issued: "Issued",
  inspections: "In inspections",
  final: "Finaled",
  closed: "Closed",
  cancelled: "Cancelled",
  rejected: "Rejected",
};

export const permitStatusLabel = (s: string) => STATUS_LABELS[s] ?? s;

// ---------- queries ----------
export function useJobPermits(jobId: string | undefined) {
  return useQuery({
    queryKey: ["permits", "by-job", jobId],
    enabled: !!jobId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("permits" as any)
        .select("*, jurisdiction:permit_jurisdictions(*)")
        .eq("job_id", jobId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as Array<Permit & { jurisdiction: Jurisdiction | null }>;
    },
  });
}

export function useOrgPermits(orgId: string | undefined) {
  return useQuery({
    queryKey: ["permits", "by-org", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("permits" as any)
        .select("*, jurisdiction:permit_jurisdictions(*), job:jobs(id, customer_name, address)")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      // deno-lint-ignore no-explicit-any
      return (data ?? []) as any[];
    },
  });
}

export function useJurisdictions() {
  return useQuery({
    queryKey: ["permit-jurisdictions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("permit_jurisdictions" as any)
        .select("*")
        .order("state")
        .order("name");
      if (error) throw error;
      return (data ?? []) as unknown as Jurisdiction[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function usePermit(permitId: string | undefined) {
  return useQuery({
    queryKey: ["permit", permitId],
    enabled: !!permitId,
    queryFn: async () => {
      const [{ data: permit }, { data: insp }, { data: fees }, { data: filings }] =
        await Promise.all([
          supabase
            .from("permits" as any)
            .select("*, jurisdiction:permit_jurisdictions(*)")
            .eq("id", permitId!)
            .single(),
          supabase
            .from("permit_inspections" as any)
            .select("*")
            .eq("permit_id", permitId!)
            .order("scheduled_at", { ascending: true }),
          supabase
            .from("permit_fees" as any)
            .select("*")
            .eq("permit_id", permitId!)
            .order("created_at", { ascending: true }),
          supabase
            .from("permit_filings" as any)
            .select("*")
            .eq("permit_id", permitId!)
            .order("created_at", { ascending: false }),
        ]);
      return {
        permit: (permit ?? null) as unknown as
          | (Permit & { jurisdiction: Jurisdiction | null })
          | null,
        inspections: (insp ?? []) as unknown as PermitInspection[],
        fees: (fees ?? []) as unknown as PermitFee[],
        filings: (filings ?? []) as unknown as PermitFiling[],
      };
    },
  });
}

// ---------- mutations ----------
export function useCreatePermit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      job_id: string;
      org_id: string;
      jurisdiction_id?: string | null;
      permit_type?: string;
      scope_summary?: string;
      property_address?: string;
      created_by: string;
    }) => {
      const { data, error } = await supabase
        .from("permits" as any)
        .insert({
          job_id: input.job_id,
          org_id: input.org_id,
          jurisdiction_id: input.jurisdiction_id ?? null,
          permit_type: input.permit_type ?? "building",
          scope_summary: input.scope_summary ?? null,
          property_address: input.property_address ?? null,
          created_by: input.created_by,
          status: "draft",
        })
        .select()
        .single();
      if (error) throw error;
      return data as unknown as Permit;
    },
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["permits", "by-job", p.job_id] });
      qc.invalidateQueries({ queryKey: ["permits", "by-org"] });
      toast.success("Permit created");
    },
    onError: (e) => toast.error(`Could not create permit: ${(e as Error).message}`),
  });
}

export function useUpdatePermit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: Partial<Permit> }) => {
      const { error } = await supabase
        .from("permits" as any)
        .update(input.patch)
        .eq("id", input.id);
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["permit", vars.id] });
      qc.invalidateQueries({ queryKey: ["permits"] });
    },
  });
}

export function useAutofillPermit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (permit_id: string) => {
      const { data, error } = await supabase.functions.invoke("permit-autofill", {
        body: { permit_id },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error ?? "autofill_failed");
      return data.payload as Record<string, unknown>;
    },
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["permit", id] });
      toast.success("Application drafted by Bridgette");
    },
    onError: (e) => toast.error(`Auto-fill failed: ${(e as Error).message}`),
  });
}

export function useFilePermit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { permit_id: string; pricing_cents?: number }) => {
      const { data, error } = await supabase.functions.invoke("permit-file", {
        body: input,
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error ?? "filing_failed");
      return data as { mode: "automated" | "concierge"; filing_id: string; message?: string };
    },
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: ["permit", vars.permit_id] });
      if (res.mode === "automated") toast.success("Filing started — running in the background");
      else toast.message("Queued for human filing", { description: res.message });
    },
    onError: (e) => toast.error(`Could not start filing: ${(e as Error).message}`),
  });
}

export function useSyncSdlPermits() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { org_id: string; credential_id?: string }) => {
      const { data, error } = await supabase.functions.invoke("permit-sync-sdl", {
        body: input,
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error ?? data?.message ?? "sync_failed");
      return data as {
        ok: true;
        scanned_rows: number;
        candidate_permits: number;
        inserted: number;
        updated: number;
        message: string;
      };
    },
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: ["permits", "by-org", vars.org_id] });
      toast.success(res.message);
    },
    onError: (e) => toast.error(`Sync failed: ${(e as Error).message}`),
  });
}

export function useSavePortalCredentials() {
  return useMutation({
    mutationFn: async (input: {
      org_id: string;
      jurisdiction_id?: string | null;
      label?: string;
      username: string;
      password: string;
      consent_to_automate: boolean;
    }) => {
      const { data, error } = await supabase.functions.invoke("permit-credentials-save", {
        body: input,
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error ?? "save_failed");
      return data.credential;
    },
    onSuccess: () => toast.success("SDL credentials saved (encrypted)"),
    onError: (e) => toast.error(`Could not save credentials: ${(e as Error).message}`),
  });
}

export function useAddInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      permit_id: string;
      org_id: string;
      inspection_type: string;
      scheduled_at?: string;
    }) => {
      const { error } = await supabase.from("permit_inspections" as any).insert({
        permit_id: input.permit_id,
        org_id: input.org_id,
        inspection_type: input.inspection_type,
        scheduled_at: input.scheduled_at ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["permit", vars.permit_id] });
      toast.success("Inspection scheduled");
    },
  });
}

export function useAddFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      permit_id: string;
      org_id: string;
      kind: string;
      amount: number;
      description?: string;
    }) => {
      const { error } = await supabase.from("permit_fees" as any).insert({
        permit_id: input.permit_id,
        org_id: input.org_id,
        kind: input.kind,
        amount: input.amount,
        description: input.description ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["permit", vars.permit_id] });
      toast.success("Fee logged");
    },
  });
}
